import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import type { TableColumnsType, TableProps } from 'antd';
import { Button, message, Space, Table, Tag } from 'antd';
import { Form, Formik } from 'formik';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DEFAULT_PAGE_SIZE, TEXT_BUTTON_HEADER_CREATE } from '../../../config/config';
import useQueryParams, { buildParamsFromQuery } from '../../../hooks/useQueryParams';
import { PermissionCampaigns } from '../../../permissions/PermissionCampaigns';
import campaingStore from '../../../stores/campaingStore';
import { Campaing } from '../../../types/Campaing';
import { formatDateString } from '../../../utils/dateFunctions';
import { exportToExcel } from '../../../utils/excelFunction';
import { getSortField, getSortOrder, getUniqueFilters, renderRestrictionsByClass } from '../../../utils/functions';
import { StatusActive } from '../../../utils/selects';
import { getStatusColor } from '../../../utils/statusColorMapper';
import BreadCrumbComponent from '../../atoms/BreadCrumbComponent';
import ActionButton from '../../atoms/ButtonComponent';
import InputComponet from '../../atoms/InputComponet';
import PermissionComponent from '../../atoms/PermissionComponent';
import SelectComponent from '../../atoms/SelectComponent';
import ContentBox from '../../molecules/ContentBox';
import Permission from '../../molecules/Permission';
import ModalComponent from '../../organisms/ModalComponent';
import { useAuth } from '../../protected/ProviderAuth';

interface DataType {
    active: boolean | React.Key;
    document: string;
    type: string;
    name: string;
    uuid: string;
    key: React.Key;
}

interface QueryParams {
    name?: string,
    active?: string,
    size?: number;
    page?: number;
    sort?: string;
    search?: string;
}

// BreadCrumb
const breadCrumb = [
    {
        title: <Link to="/dashboard">Dashboard</Link>,
    },
    {
        title: <p>Listagem de Campanhas</p>,
    },
]

const ReportCartAbandonment: React.FC = observer(() => {
    const { campaignList, loading, page, totalElements, defaultparams, sort, size } = campaingStore;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
    const [itemSelected, setItemSelected] = useState<any>();
    const navigate = useNavigate();
    const [selectionCheckbox, setSelectionCheckbox] = useState<any>();
    const [queryParams, setQueryParams] = useQueryParams();
    const listRouter = () => { navigate(`/campanha/cadastro`); }
    const { hasPermission, isColumnVisible } = useAuth();

    const mappings = {
        page: 'page',
        size: 'size',
        sort: 'sort',
        name: 'name',
        active: 'active',
    };

    const defaultparamsList = {
        size: 999999,
        page: 0,
        sort: 'created,desc',
        search: '',
    }

    useEffect(() => {
        const params = buildParamsFromQuery(queryParams, mappings, ":", ";");

        const fetchData = async () => {
            if (hasPermission(PermissionCampaigns.GET)) {
                await campaingStore.getList(params);
            }
        };

        fetchData();
    }, []);

    // Colunas da Tabela
    const columns: TableColumnsType<DataType> = [
        {
            title: 'Nome da campanha',
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            filters: getUniqueFilters(campaignList, 'name'),
            onFilter: (value, record) => record.name.indexOf(value as string) === 0,
            defaultSortOrder: getSortOrder('name', queryParams.sort),
        },
        {
            title: 'Modelo',
            dataIndex: 'type',
            key: 'type',
            sorter: true,
            filters: getUniqueFilters(campaignList, 'type'),
            onFilter: (value, record) => record.type.indexOf(value as string) === 0,
            defaultSortOrder: getSortOrder('type', queryParams.sort),
        },
        {
            title: 'Periodo de Atividade',
            dataIndex: 'restrictions',
            key: 'restrictions',
            render: (restrictions) => {
                return (
                    //@ts-ignore
                    <>{<p>{renderRestrictionsByClass(restrictions, "br.com.stoom.kernel.model.persistence.restriction.DateRestriction")}</p>}</>
                );
            },
        },
        {
            title: 'Data de criação',
            dataIndex: 'created',
            key: 'created',
            sorter: true,
            defaultSortOrder: getSortOrder('created', queryParams.sort),
            render: (text) => {
                return (
                    //@ts-ignore
                    <>{<p>{formatDateString(text)}</p>}</>
                );
            },
        },
        {
            title: 'Ativos',
            dataIndex: 'active',
            key: 'active',
            sorter: true,
            filters: [
                { text: 'Ativo', value: true },
                { text: 'Inativo', value: false },
            ],
            onFilter: (value, record) => record.active === value,
            defaultSortOrder: getSortOrder('active', queryParams.sort),
            render: (active) => {
                let text = active ? 'Ativo' : 'Inativo';
                return (
                    <Tag color={getStatusColor(text)}><p>{text}</p></Tag>
                );
            },
        },
        {
            title: 'Editar',
            dataIndex: 'edit',
            key: 'edit',
            align: 'center',
            width: 50,
            hidden: !isColumnVisible(PermissionCampaigns.GET_UUID),
            render: (text, record) => (
                <Space className="cursor-pointer" onClick={() => navigate(`/campanha/editar/${record.uuid}`)}>
                    <div><EditOutlined /></div>
                </Space>
            ),
        },
        {
            title: 'Deletar',
            dataIndex: 'delete',
            key: 'delete',
            align: 'center',
            width: 50,
            hidden: !isColumnVisible(PermissionCampaigns.DELETE),
            render: (text, record) => (
                <Space className="cursor-pointer" onClick={() => showModal(record)}>
                    <div ><DeleteOutlined /></div>
                </Space>
            ),
        },
    ];

    const showModal = (record: any) => {
        setItemSelected(record);
        setIsModalOpen(true);
    };

    const handleDeleteItem = async () => {
        const response = await campaingStore.deleteCampaing(itemSelected.uuid);

        if (!response?.error) {
            campaingStore.getList(defaultparams);
            setIsModalOpen(false);
        }
    };

    // Interação da Tabela como sorte
    const onChange: TableProps<DataType>['onChange'] = async (pagination, filters, sorter, extra) => {
        const params = buildParams(pagination, sorter, queryParams);
        await campaingStore.getList(params);
    };


    const buildParams = (
        pagination: any,
        sorter: any,
        queryParams: QueryParams
    ) => {
        const getSort = getSortField(sorter);
        const currentPage = pagination.current - 1;
        const newPageSize = pagination.pageSize || DEFAULT_PAGE_SIZE;

        const filters = buildParamsFromQuery(queryParams, mappings, ":", ";");

        const params = {
            size: pagination.pageSize || queryParams.size || newPageSize,
            page: pagination.current ? currentPage : queryParams.page || 0,
            sort: getSort || queryParams.sort || sort || 'created,desc',
            search: filters.search || queryParams.search || '',
        };

        // Atualiza a URL com os novos parâmetros
        setQueryParams({
            size: params.size,
            page: params.page,
            sort: params.sort,
            search: params.search,
        });

        return params;
    };


    const initialValues = {
        active: typeof queryParams.active === 'string'
            ? { value: queryParams.active, label: queryParams.active }
            : { value: '', label: '' },
        name: Array.isArray(queryParams.name) ? queryParams.name[0] || '' : queryParams.name || '',
    };

    // Faz o filtro da tabela
    const handleSubmit = async (values: {
        name: string;
        active: { value: string; label: string };
    }) => {
        const newParams: any = {
            active: values.active.value || undefined,
            name: values.name || undefined,
        };

        const search = [
            values.active.value ? `,active:"${values.active.value}"` : null,
            values.name ? `;name:"${values.name}"` : null,
        ]
            .filter(Boolean)
            .join(' ');

        const params = {
            size: queryParams.size ? Number(queryParams.size) : DEFAULT_PAGE_SIZE,
            page: 0,
            sort: queryParams.sort || sort || 'created,desc',
            search,
        };

        setQueryParams(newParams);

        //@ts-ignore
        await campaingStore.getList(params);
    };

    // Limpa os dados do formulario
    const handleClearAndSearch = async (resetForm: () => void) => {
        resetForm();
        setQueryParams({});
        await campaingStore.getList(defaultparams);
    };

    const rowSelection = {
        onChange: (selectedRowKeys: React.Key[], selectedRows: DataType[]) => {
            setSelectionCheckbox(selectedRows);
        },
    };


    const deleteSelectedItems = async () => {
        try {
            if (selectionCheckbox !== undefined) {
                const results = await Promise.all(
                    selectionCheckbox.map((item: any) => campaingStore.deleteCampaingAll(item.uuid))
                );
                const hasError = results.some(response => response?.error);
                if (hasError) {
                    message.error('Erro ao deletar um ou mais Campanhas.');
                    setSelectionCheckbox([]);
                } else {
                    setSelectionCheckbox([]);
                    message.success('Todos as Campanhas foram deletados com sucesso!');
                    await campaingStore.getList(defaultparams);
                }
                setIsModalDeleteOpen(false);
            } else {
                message.error('Selecione pelo menos um item da listagem');
            }
        } catch (error) {
            message.error('Erro ao tentar deletar os Campanhas.');
        }
    };

    const handleExport = async () => {
        const params = buildParamsFromQuery(queryParams, mappings, ":", ";");
        const exportParams = {
            ...params,
            size: 999999,
        }

        const dataExport = await campaingStore.getList(exportParams);

        if (dataExport) {
            let campaignsList = [] as any;
            dataExport.data.content.map((item: Campaing) => {
                let dateRestriction;
                let formattedMaxDate;

                // Encontre a restrição de data, se existir
                const dateRestrictionObj = item.restrictions.find((r: any) =>
                    r?.['@class'].includes('DateRestriction')
                );

                // Use dateToText para formatar minDate e maxDate
                if (dateRestrictionObj) {
                    const formattedMinDate = formatDateString(
                        dateRestrictionObj.minDate
                    );
                    if (dateRestrictionObj.maxDate === '2121-05-02T21:00:00.000-03:00') {
                        formattedMaxDate = 'Sem limite';
                    } else {
                        formattedMaxDate = formatDateString(
                            dateRestrictionObj.maxDate
                        );
                    }
                    dateRestriction = `${formattedMinDate} - ${formattedMaxDate}`;
                }
                campaignsList.push({
                    name: item.name,
                    userName: item.type,
                    dateRestriction: dateRestriction,
                    active: item.active === false ? 'Inativo' : 'Ativo',
                    created: formatDateString(item.created, '/', true),
                });
            });

            exportToExcel(campaignsList, [
                'Nome da campanha',
                'Modelo',
                'Periodo de Atividade',
                'Canal',
                'Status',
                'Data de Criação',
            ], "Tabela de Campanhas.xlsx");
        }
    };


    const deleteAllModal = () => {
        if (selectionCheckbox?.length > 0) {
            setIsModalDeleteOpen(true);
        } else {
            message.warning('Por favor, selecione ao menos um item para realizar a exclusão.');
        }
    }

    return (
        <>
            <BreadCrumbComponent breadCrumbList={breadCrumb} permission={PermissionCampaigns.POST} textButton={TEXT_BUTTON_HEADER_CREATE} onClick={() => listRouter()} />

            <Permission permissionKeys={[PermissionCampaigns.GET]} fallback={<PermissionComponent />}>
                <ContentBox className="mb-4 p-6" title="Listagem de Campanhas" description='Tela de listagem de Campanhas'>
                    <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
                        {({ resetForm }) => (  // Passa o resetForm do render prop de Formik
                            <Form>
                                <div className="flex items-end w-full gap-4 pb-4">
                                    <div className="w-full">
                                        <InputComponet label="Nome" name="name" id="name" />
                                    </div>
                                    <div className="w-full">
                                        <SelectComponent label="Status" name="active" options={StatusActive} />
                                    </div>
                                </div>
                                <div className="flex items-end gap-4 justify-end">
                                    <Button onClick={() => handleClearAndSearch(resetForm)}>Limpar</Button>
                                    <Button htmlType="submit" type="primary">Filtrar</Button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </ContentBox>

                <ContentBox className="mb-4 p-6" description='Tabela de Campanhas' actions={
                    <>
                        <Permission permissionKeys={[PermissionCampaigns.DELETE]}><ActionButton icon actionType="delete" onClick={() => deleteAllModal()}>Deletar</ActionButton></Permission>
                        <ActionButton icon actionType="export" onClick={() => handleExport()}>Exportar para Excel</ActionButton>
                    </>
                }>
                    <Table
                        className="table-custom"
                        columns={columns}
                        dataSource={campaignList.map((item: any, index: number) => ({
                            ...item,
                            key: item.uuid || index,
                        }))}
                        onChange={onChange}
                        rowSelection={rowSelection}
                        loading={loading}
                        size="middle"
                        bordered={false}
                        pagination={{
                            current: page + 1,
                            pageSize: size,
                            total: totalElements,
                            showSizeChanger: true,
                            pageSizeOptions: ['20', '40', '60'],
                        }}
                        showSorterTooltip={{ target: 'sorter-icon' }}
                    />
                </ContentBox>
            </Permission>

            {isModalOpen && <ModalComponent
                title="Aviso"
                content={<p>Você deseja excluir a Campanha <b>{itemSelected?.name}</b>?</p>}
                isModalOpen={isModalOpen}
                handleOk={handleDeleteItem}
                handleCancel={() => setIsModalOpen(false)}
            />}

            {isModalDeleteOpen && <ModalComponent
                title="Aviso"
                content={<p>Você está prestes a excluir vários itens da listagem, deseja prosseguir?</p>}
                isModalOpen={isModalDeleteOpen}
                handleOk={deleteSelectedItems}
                handleCancel={() => setIsModalDeleteOpen(false)}
            />}
        </>
    );
});

export default ReportCartAbandonment;